<template>
  <div v-if="listingLinkHref" :class="$style.linkItem">
    <a
      :class="$style.link"
      :href="listingLinkHref"
      rel="noopener noreferrer"
      target="_blank"
      @click="onListingLinkClick"
    >
      <div :class="$style.linkLabel">{{ listingLinkLabel }}</div>
      <HgIcon :class="$style.icon" icon="ExternalLink" theme="secondary" />
    </a>
  </div>
</template>

<script lang="ts" setup>
import { sanitizeUrl } from '@/helpers/Sanitize/Sanitize';
import { HgIcon } from '@smg-real-estate/domus';
import { Url } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineOptions({
  name: 'ListingLink',
});

interface Props {
  listingLink: Url;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  trackOutboundLink: [url: Url];
}>();

const listingLinkHref = computed((): string | null => {
  return sanitizeUrl(props.listingLink.value, { allowRelative: true }) ?? null;
});
const listingLinkLabel = computed((): string => {
  return props.listingLink.title ?? (t('listingLink.defaultLabel') as string);
});

const onListingLinkClick = () => {
  const formattedExternalWebsite: Url = {
    value: listingLinkHref.value ?? undefined,
    title: listingLinkLabel.value,
  };
  emit('trackOutboundLink', formattedExternalWebsite);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.linkItem {
  @include body-2;

  line-height: 1.25;
  vertical-align: middle;
}

.link {
  display: flex;
  align-items: center;
  color: var(--theme-color-secondary);
}

.linkLabel {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon {
  flex-shrink: 0;
  margin-left: $space-25;
}
</style>
